import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { toJsonLd } from '../lib/html';
import * as siteFlags from '../config/siteFlags';
import seo from '../config/seoPages.json';

// Per-page titles, descriptions and share cards live in seoPages.json (also baked into HTML by scripts/seo-prerender.js).
const SITE = seo.site;
const PAGES = Object.fromEntries(seo.pages.map((p) => [p.path, p]));
const ORG_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'CHIRE',
  legalName: 'ConsultMeIn Inc',
  url: 'https://www.chire.co',
  logo: 'https://www.chire.co/chire-logo-full.png',
  description: 'CHIRE — Hiring Intelligence & Recruitment Engine, an HR-tech product of ConsultMeIn Inc.',
  sameAs: [
    'https://www.linkedin.com/company/chire/',
    'https://www.facebook.com/getchire',
  ],
};

// Routes that should NOT be indexed (authenticated app + transactional pages).
const NOINDEX_PREFIXES = [
  '/dashboard', '/jobs', '/candidates', '/pipeline', '/analytics', '/settings',
  '/billing', '/jd-builder', '/assessments', '/take-assessment', '/interviews',
  '/join-interview', '/portal', '/admin', '/verification', '/verify',
  '/resume-intelligence', '/credentials', '/compliance', '/payment', '/auth',
];

export default function RouteSeo() {
  const { pathname } = useLocation();
  const path = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  const page = PAGES[path];
  const live = Boolean(page) && (!page.flag || siteFlags[page.flag] === true);
  const meta = page || seo.defaults;
  const noindex = page ? !live : NOINDEX_PREFIXES.some((p) => path === p || path.startsWith(p + '/'));
  const canonical = `${SITE}${path}`;
  const image = `${SITE}${meta.image}`;

  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta name="author" content="CHIRE by ConsultMeIn" />
      {live && <link rel="canonical" href={canonical} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="CHIRE" />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      {live && <meta property="og:url" content={canonical} />}
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={meta.title} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={image} />
      <script type="application/ld+json">{toJsonLd(ORG_JSONLD)}</script>
      {live && page.jsonld && <script type="application/ld+json">{toJsonLd(page.jsonld)}</script>}
    </Helmet>
  );
}
